/**
 * Sector operations — Sample Chain of Custody Common Data Model and Validators.
 * R39 M4 (plan docs/plans/sector-operations.md §2 SO-P262, row M4, decisions SO-D228, SO-D229, SO-D232).
 * Clinics diagnostics & collection centres sample tracking.
 */

import type { JobScheduleInput, OpsJobGeo, OpsJobGeoStatus } from './requests.js'

export const SAMPLE_BAG_STAGES = [
  'COLLECTED',
  'AT_CENTRE',
  'WITH_RUNNER',
  'AT_LAB',
  'CLOSED',
] as const

export type SampleBagStage = (typeof SAMPLE_BAG_STAGES)[number]

export const SAMPLE_BARCODE_REGEX = /^[A-Za-z0-9_-]{4,40}$/
export const SAMPLE_PHOTO_PATH_REGEX =
  /^workspaces\/[A-Za-z0-9_-]+\/ops_cli_sample_bags\/[A-Za-z0-9_-]+\/[A-Za-z0-9_-]+\/[A-Za-z0-9_.-]+$/
export const SAMPLE_RUNNER_PHONE_REGEX = /^\+[1-9]\d{7,14}$/

export interface SampleBagRejectedItem {
  barcode: string
  reasonKey: string
  redrawRequestId?: string
  rejectedAt?: number
}

export interface SampleBag {
  id: string
  workspaceId: string
  placeId: string // collection centre / clinic place
  barcodes: string[]
  receivedBarcodes?: string[]
  stage: SampleBagStage
  stageAt: Partial<Record<SampleBagStage, number>>
  coldBoxPhotoPath?: string | null
  rejected?: SampleBagRejectedItem[]
  assignedRunnerPhone?: string | null
  assignedRunnerUid?: string | null
  homeCollectionJobId?: string | null
  createdAt?: unknown
  createdBy?: string
  updatedAt?: unknown
  updatedBy?: string
  closedAt?: unknown
  closedBy?: string
}

export interface SampleRunner {
  name: string
  phone: string // E.164
  uid?: string
}

export interface SampleSettingsDoc {
  workspaceId: string
  stageLimitsMinutes?: Partial<Record<SampleBagStage, number>>
  runners?: SampleRunner[]
  updatedAt?: unknown
  updatedBy?: string
}

export const DEFAULT_SAMPLE_STAGE_MINUTES: Record<SampleBagStage, number> = {
  COLLECTED: 60,
  AT_CENTRE: 120,
  WITH_RUNNER: 180,
  AT_LAB: 240,
  CLOSED: 0,
}

export function isValidSampleBarcode(barcode: unknown): barcode is string {
  return typeof barcode === 'string' && SAMPLE_BARCODE_REGEX.test(barcode.trim())
}

export function isValidSamplePhotoPath(path: unknown): boolean {
  return typeof path === 'string' && SAMPLE_PHOTO_PATH_REGEX.test(path.trim())
}

export function isValidRunnerPhone(phone: unknown): boolean {
  return typeof phone === 'string' && SAMPLE_RUNNER_PHONE_REGEX.test(phone.trim())
}

export const ALLOWED_SAMPLE_STAGE_TRANSITIONS: Record<SampleBagStage, readonly SampleBagStage[]> = {
  COLLECTED: ['AT_CENTRE', 'WITH_RUNNER'],
  AT_CENTRE: ['WITH_RUNNER'],
  WITH_RUNNER: ['AT_LAB'],
  AT_LAB: ['CLOSED'],
  CLOSED: [],
} as const

export function canTransitionSampleStage(
  from: SampleBagStage,
  to: SampleBagStage,
  bag?: { barcodes: string[]; receivedBarcodes?: string[]; rejected?: SampleBagRejectedItem[] },
): { allowed: boolean; reason?: string } {
  if (from === to) {
    return { allowed: false, reason: 'Already in this stage' }
  }
  const allowedNext = ALLOWED_SAMPLE_STAGE_TRANSITIONS[from] || []
  if (!allowedNext.includes(to)) {
    return {
      allowed: false,
      reason: `Cannot transition sample bag from ${from} to ${to}: transition must be forward-only`,
    }
  }
  if (to === 'CLOSED') {
    if (from !== 'AT_LAB') {
      return { allowed: false, reason: 'CLOSED is only allowed from AT_LAB' }
    }
    if (bag) {
      const received = new Set(bag.receivedBarcodes || [])
      const rejected = new Set((bag.rejected || []).map((r) => r.barcode))
      const allAccounted = bag.barcodes.every((bc) => received.has(bc) || rejected.has(bc))
      if (!allAccounted) {
        return {
          allowed: false,
          reason: 'Cannot close sample bag: every barcode must be received or rejected at the lab',
        }
      }
    }
  }
  return { allowed: true }
}

export function getSampleStageLimitMinutes(
  stage: SampleBagStage,
  workspaceSettings?: SampleSettingsDoc,
): number {
  if (workspaceSettings?.stageLimitsMinutes?.[stage] !== undefined) {
    return workspaceSettings.stageLimitsMinutes[stage]!
  }
  return DEFAULT_SAMPLE_STAGE_MINUTES[stage] ?? 0
}

export interface SampleHandoverDoc {
  id: string
  workspaceId: string
  bagId: string
  tokenHash: string
  expiresAtMs: number
  status: 'ACTIVE' | 'CONFIRMED' | 'EXPIRED'
  createdByUid: string
  createdAtMs: number
  confirmedAtMs?: number
  otpVerifiedPhone?: string
}

export interface CreateSampleBagInput {
  workspaceId: string
  placeId: string
  barcodes: string[]
  homeCollectionJobId?: string
}

export interface AttachColdBoxPhotoInput {
  workspaceId: string
  bagId: string
  photoPath: string
}

export interface StartSampleHandoverInput {
  workspaceId: string
  bagId: string
}

export interface StartSampleHandoverResult {
  handoverId: string
  token: string
  expiresAtMs: number
  url: string
}

export interface GetSampleHandoverInfoInput {
  token: string
}

export interface GetSampleHandoverInfoResult {
  handoverId: string
  workspaceName: string
  bagId: string
  placeName: string
  barcodes: string[]
  maskedRunnerPhones: string[]
}

export interface PrepareSampleHandoverOtpInput {
  token: string
  phone: string
}

export interface PrepareSampleHandoverOtpResult {
  success: boolean
  attemptsRemaining: number
}

export interface ConfirmSampleHandoverInput {
  token: string
  barcodes: string[]
}

export interface ConfirmSampleHandoverResult {
  bagId: string
  confirmedCount: number
  status: 'WITH_RUNNER'
}

export interface ReceiveSamplesAtLabInput {
  workspaceId: string
  bagId: string
  receivedBarcodes: string[]
  rejectedBarcodes?: Array<{ barcode: string; reasonKey: string }>
}

export interface ReceiveSamplesAtLabResult {
  bagId: string
  stage: SampleBagStage
  receivedCount: number
  rejectedCount: number
  redrawRequestIds: string[]
}

export interface CreateHomeCollectionJobInput {
  workspaceId: string
  placeId: string
  patientAddress: string
  bookingToken?: string
  assigneeUid?: string
  schedule?: JobScheduleInput | null
  opId?: string
}

export interface CreateHomeCollectionJobResult {
  requestId: string
  bookingToken?: string
  assigneeUid?: string
}

export interface GetHomeCollectionAddressInput {
  workspaceId: string
  requestId: string
}

export interface GetHomeCollectionAddressResult {
  address: string
}

export type HomeCollectionEventKind = 'ARRIVED' | 'SAMPLE_COLLECTED' | 'HANDOFF'

export interface RecordRunnerPingInput {
  workspaceId: string
  requestId: string
  event: HomeCollectionEventKind
  geo: OpsJobGeo
  geoStatus?: OpsJobGeoStatus
}

export interface RecordRunnerPingResult {
  success: boolean
  event: HomeCollectionEventKind
  recordedAt: number
}
